import { inject, Injectable } from "@angular/core";
import { createEffect, ofType, Actions, ROOT_EFFECTS_INIT } from "@ngrx/effects";
import { exhaustMap, map, catchError, of, tap, EMPTY, switchMap, concatMap, withLatestFrom } from "rxjs";
import { UserActions } from "./user.actions";
import { UserService } from "../../services/user/user.service";
import { Router } from "@angular/router";
import { AlertSocketService } from "../../services/alert/sockets.service";
import { Store } from "@ngrx/store";
import { AppState } from "../root";
import { selectMediaMTXToken, selectUser } from "./user.selectors";
import { ToastService } from "../../services/toast/toast.service";
import { ILoggedInUser, LoggedInUser, ToastType } from "../../models";
import { HttpStatusCode } from "@angular/common/http";
import { SystemAlertsService } from "../../services/system-alerts/system-alerts.service";
import { AlertLockActions } from "../alert/alert.actions";
import { ActivityService } from "../../services/activity/activity.service";
import { LogRocketService } from "../../services/log-rocket/log-rocket.service";
import { IMediaMTXToken } from "../../models/auth.model";
import { DateTime } from "luxon";
import { peekTrackedSearch } from "../../../modules/alerts/filter";

interface ILoginResponse {
  auth_token: string;
}

interface IFetchUserResponse {
  data: ILoggedInUser & { pic?: unknown };
}

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions);
  private toastService = inject(ToastService);
  private systemNotificationsService = inject(SystemAlertsService);
  private activityService = inject(ActivityService);
  private logRocketService = inject(LogRocketService);
  private store$ = inject(Store<AppState>);
  private userService = inject(UserService);
  private socketService = inject(AlertSocketService);
  private router = inject(Router);

  loginUser$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.login),
    exhaustMap(action => this.userService.login(action.username, action.password)
      .pipe(
        map((response) => {
          const authToken = (response as ILoginResponse).auth_token;
          this.userService.setToken(authToken)
          return UserActions.fetchUser({ token: authToken })
        }),
        catchError(error => of(UserActions.fetchUserFailure({ error })))
      ))
  )
  );

  fetchUser$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.fetchUser),
    exhaustMap((action) => {
      if (action.token) {
        this.userService.setToken(action.token);
        this.store$.dispatch(UserActions.setToken({ token: action.token }));
      }

      return this.userService.fetchUser()
        .pipe(
          map((response) => {
            const userData = (response as IFetchUserResponse).data
            delete userData.pic
            userData.isAdmin = false

            for (const role of userData.roles) {
              if (role.name.toLowerCase() === 'administrator') {
                userData.isAdmin = true;
                break;
              }
            }
            this.store$.dispatch(AlertLockActions.setUserId({ userId: userData.id }));
            return UserActions.fetchUserSuccess({ user: userData })
          }),
          catchError(error => of(UserActions.fetchUserFailure({ error })))
        )
    })
  )
  );

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.logout),
    tap(({ endedSessionId }) => {
      // NOTE: Breaking the session at the identity boundary so the next login is not
      // attributed to the previous user; a LogRocket failure must never block logout
      try {
        this.logRocketService.startNewSession();
      } catch (error) {
        console.error('LogRocket session split failed on logout', error);
      }

      this.router.navigate(["/login"]).then(() => {
        this.socketService.destroy();
        this.systemNotificationsService.destroy();
        this.activityService.ngOnDestroy();
        this.userService.logout(endedSessionId);
        sessionStorage.clear();
      })
    })), { dispatch: false }
  );

  sessionExpired$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.sessionExpired),
    tap(() => {
      this.router.navigate(["/session-expired"]).then(() => {
        this.socketService.destroy();
        this.systemNotificationsService.destroy();
        sessionStorage.clear();

        // NOTE: Disabling for now
        // To prevent failed socket connections from clearing auth key

        // localStorage.removeItem(environment.authTokenKey);
      })
    })), { dispatch: false }
  );

  fetchUserFailure$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.fetchUserFailure),
    switchMap(() => {
      // if (error.status !== HttpStatusCode.Unauthorized) {
        return of(UserActions.logout())
      // }
      // return EMPTY
    })
  ));


  fetchUserSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.fetchUserSuccess),
    concatMap((action) => {
      const user = new LoggedInUser(action.user)

      this.socketService.initialise();
      this.systemNotificationsService.initialise();

      // NOTE: A LogRocket failure must never block post-login routing
      try {
        this.logRocketService.initLogRocket(action.user);
      } catch (error) {
        console.error('LogRocket init failed on user load', error);
      }

      // Tracked-object windows capture the search at module load (the query string
      // does not survive the auth bootstrap), so route them straight to the
      // dashboard instead of the recordings popup / lastRoute restore.
      if (peekTrackedSearch() != null) {
        this.router.navigate(["/alerts"])
        if (user.isAdmin) {
          return of(UserActions.generateCode())
        }
        return of();
      }

      // Standalone help pages are opened with noopener and have no lastRoute
      // to restore in their fresh tab, so leave them on the URL they requested
      // instead of falling through to the recordings popup / alerts default.
      if (this.router.url.startsWith("/help")) {
        if (user.isAdmin) {
          return of(UserActions.generateCode())
        }
        return of();
      }

      const opener = window.opener;
      if (opener != null) {
        this.router.navigate(["/recordings"])
        return of()
      }

      const lastRoute = sessionStorage.getItem(`lastUrl_${user.id}`);
      if (lastRoute != null && lastRoute !== "/focus") {
        this.router.navigate([lastRoute]).catch(() => {
          console.error("Redirect Error")
        })
      } else {
        // HACK
        // if (!action.user.isAdmin) {
        //   this.router.navigate(["/focus"])
        // } else {
        //   this.router.navigate(["/alerts"])
        // }
        if (user.isVMSOnlyUser) {
          this.router.navigate(["/livefeed"])
        } else {
          this.router.navigate(["/alerts"])
        }
        // HACK
      }

      if (user.isAdmin) {
        return of(UserActions.generateCode())
      }

      return of();
    })
  ));

  getMediaMTXToken$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.getMediaMTXToken),
    switchMap(() => this.userService.getMediaMTXToken()
      .pipe(
        map((response: IMediaMTXToken) => {
          return UserActions.getMediaMTXTokenSuccess({ mediaMTXToken: response })
        }),
        catchError(error => of(UserActions.getMediaMTXTokenFailure({ error })))
      ))
  ));

  checkMediaMTXToken$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.checkMediaMTXToken),
    withLatestFrom(this.store$.select(selectMediaMTXToken)),
    exhaustMap(([, mediaMTXToken]) => {
      if (mediaMTXToken == null) {
        return of(UserActions.getMediaMTXToken())
      }

      const now: number = new Date(DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss')).getTime();
      const ttlTolerance: number = 60 * 1000;
      const ttlDate: number = new Date(mediaMTXToken.ttl).getTime() - ttlTolerance;

      if (now > ttlDate) {
        return of(UserActions.getMediaMTXToken())
      }
      return of(UserActions.getMediaMTXTokenSuccess({ mediaMTXToken: mediaMTXToken }))
    })
  ));

  generateCode$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.generateCode),
    withLatestFrom(this.store$.select(selectUser)),
    concatMap(([, user]) => {
      if (user == null) {
        return EMPTY
      }
      return this.userService.generateCode(user).pipe(
        switchMap(() => {
          return of(UserActions.getCode())
        })
      )
    })
  ));

  getCode$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.getCode),
    withLatestFrom(this.store$.select(selectUser)),
    concatMap(([, user]) => {
      if (user == null) {
        return of(UserActions.getCodeFailure({ error: Error("Invalid User") }))
      }
      return this.userService.getCode(user).pipe(
        map(code => UserActions.getCodeSuccess({ code: code })),
        catchError(error => of(UserActions.getCodeFailure({ error })))
      );
    })
  ));

  verifyCode$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.verifyCode),
    withLatestFrom(this.store$.select(selectUser)),
    concatMap(([action, user]) => {
      if (user == null) {
        return of(UserActions.verifyCodeFailure({ error: Error("Invalid User") }))
      }
      return this.userService.verifyCode(user, action.code).pipe(
        map(() => {
          this.toastService.showToast("Admin acess granted", ToastType.SUCCESS)
          return UserActions.verifyCodeSuccess()
        }),
        catchError(error => {
          let msg = "Failed to verify code"
          if (error.status === HttpStatusCode.BadRequest) {
            msg = "Invalid admin code"
          }
          this.toastService.showToast(msg, ToastType.ERROR)
          return of(UserActions.verifyCodeFailure({ error: Error(msg) }))
        })
      );
    })
  ));

  init$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    map(() => {
      const token = this.userService.getToken();

      if (token != null) {
        return UserActions.fetchUser({ token: token });
      }

      return UserActions.logout();
    })
  )
  );
}
